<!--
  LookupField — computed `lookup` renderer; the chrome-free inner GridCell hosts.
  Display-only (a lookup is derived, never stored): it formats `modelValue` at
  the cell's text size and renders the same in every `mode` (the emits exist
  only for contract uniformity). The open overlay wraps the value in full
  instead of ellipsising.
-->
<template>
  <div class="lookup-field" :class="sizeClass">
    <span>{{ displayedValue }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { type Field, formatValue } from "@auger/core/fields"
import { type FieldMode, type FieldSize, textSizeClass } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

// Declared for contract uniformity with editable inners; never fired.
defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const sizeClass = computed(() => textSizeClass(props.size))

// The enriched field's `format` renders each looked-up value and blanks empty,
// so an empty lookup reads blank like any other empty cell.
const displayedValue = computed(() => formatValue(props.field, props.modelValue) ?? "")
</script>

<style>
.lookup-field {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 100%;

  span {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
}

/* In the open overlay the value wraps and shows in full instead of ellipsising. */
.grid-editor .lookup-field span {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}
</style>
